import DOMPurify, { type Config } from "dompurify";
import { marked } from "marked";

/**
 * Markdown profile for AI chat replies. The model's context includes CRM note
 * and email text, and `marked` passes raw HTML in its input straight through,
 * so a reply can carry attacker markup.
 *
 * - HTML profile only (no SVG/MathML).
 * - No forms or form controls, `<style>`, frames or plugins.
 * - No `style` and no `class` from the model: with Tailwind on the page,
 *   `class="fixed inset-0 z-50 bg-white"` builds a full-page overlay. The
 *   app's own classes are applied in the hook below, after sanitizing, so
 *   they are the only classes in the output.
 */
const CHAT_MARKDOWN_CONFIG: Config = {
  FORBID_ATTR: ["style", "class"],
  FORBID_TAGS: [
    "form",
    "input",
    "button",
    "select",
    "textarea",
    "option",
    "style",
    "iframe",
    "object",
    "embed",
  ],
  USE_PROFILES: { html: true },
};

// Tailwind classes for markdown elements. Lists use OUTSIDE markers (no
// list-inside): with list-inside, a loose-list item — <li><p>…</p></li>, which
// marked emits whenever items are separated by blank lines — renders the marker
// alone on its own line with the text dropping below it. Outside markers sit in
// the gutter aligned to the first line, so "1." stays beside its text.
const CLASS_BY_TAG: Record<string, string> = {
  A: "text-eggplant-600 dark:text-eggplant-400 underline decoration-2 hover:text-eggplant-700 dark:hover:text-eggplant-300 font-medium transition-colors",
  CODE: "bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm",
  LI: "mb-1",
  OL: "list-decimal ml-5 my-2 space-y-1",
  P: "mb-3",
  PRE: "bg-gray-100 dark:bg-gray-800 p-3 rounded-md overflow-x-auto my-2",
  STRONG: "font-semibold",
  UL: "list-disc ml-5 my-2 space-y-1",
};

// A dedicated instance, because DOMPurify hooks are global to an instance:
// these must not leak into other DOMPurify users (utils/sanitizeHtml).
const chatPurifier = DOMPurify(window);
chatPurifier.addHook("afterSanitizeAttributes", (node) => {
  const className = CLASS_BY_TAG[node.tagName];
  if (className) node.setAttribute("class", className);
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/** Render an AI chat reply's markdown to sanitized, styled HTML for `v-html`. */
export function renderChatMarkdown(content: string): string {
  if (!content) return "";
  const html = marked.parse(content, { async: false, breaks: true, gfm: true, pedantic: false });
  return chatPurifier.sanitize(html, CHAT_MARKDOWN_CONFIG);
}
